import React from 'react';
import { useTranslation } from '../hooks/useTranslation';

type SyncStatus = 'confirm' | 'syncing' | 'success' | 'error';

interface SyncCounterpartyConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  counterpartyName: string;
  status: SyncStatus;
  errorMessage?: string;
}

const SyncCounterpartyConfirmModal: React.FC<SyncCounterpartyConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  counterpartyName,
  status,
  errorMessage
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm();
  };

  const renderContent = () => {
    switch (status) {
      case 'confirm':
        return (
          <>
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-yellow-100 rounded-full">
              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
              {t('companies.counterparties.syncConfirmTitle') || 'Синхронизация с wFirma'}
            </h3>
            
            <p className="text-sm text-gray-600 text-center mb-6">
              {(t('companies.counterparties.syncConfirm') || 'Создать контрагента "{name}" в wFirma?').replace('{name}', counterpartyName)}
            </p>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {t('common.confirm') || 'Подтвердить'}
              </button>
            </div>
          </>
        );

      case 'syncing':
        return (
          <>
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-blue-100 rounded-full">
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
              {t('companies.counterparties.syncInProgress') || 'Синхронизация...'}
            </h3>
            
            <p className="text-sm text-gray-600 text-center mb-6">
              {t('companies.counterparties.syncInProgressMessage') || 'Пожалуйста, подождите, идет синхронизация контрагента с wFirma'}
            </p>
          </>
        );

      case 'success':
        return (
          <>
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-green-100 rounded-full">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
              {t('companies.counterparties.syncSuccessTitle') || 'Синхронизация успешна'}
            </h3>
            
            <p className="text-sm text-gray-600 text-center mb-6">
              {t('companies.counterparties.syncSuccess') || 'Контрагент успешно синхронизирован с wFirma'}
            </p>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {t('common.close') || 'Закрыть'}
              </button>
            </div>
          </>
        );

      case 'error':
        return (
          <>
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
              {t('companies.counterparties.syncErrorTitle') || 'Ошибка синхронизации'}
            </h3>
            
            <p className="text-sm text-gray-600 text-center mb-6">
              {errorMessage || t('companies.counterparties.syncError') || 'Произошла ошибка при синхронизации контрагента с wFirma'}
            </p>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {t('common.close') || 'Закрыть'}
              </button>
            </div>
          </>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default SyncCounterpartyConfirmModal;
